// filepath: /Users/shayan/MySite-UserProfile/Code/src/HelperComponents/form/components/ListDropdown.tsx
import React, { useEffect, useRef, useState } from "react";
import { normalizeSelectedKey, normalizeSelectedKeys } from "../Utility";
import { FormDropdownOption } from "../FormTypes";

type ListDropdownProps = {
  id: string;
  placeholder?: string;
  required?: boolean;
  multi?: boolean;
  value: any;
  options: FormDropdownOption[];
  onChange: (val: any) => void;
};

const ListDropdown: React.FC<ListDropdownProps> = ({
  id,
  placeholder,
  required,
  multi,
  value,
  options,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const isMulti = !!multi;

  const sk = isMulti ? undefined : normalizeSelectedKey(value, options);

  const selectedTexts: string[] = Array.isArray(value) ? value : [];
  const selectedKeys = normalizeSelectedKeys(value, options) ?? [];

  let summaryLabel: string;
  if (isMulti) {
    const selectedOptions = options.filter((o) => selectedKeys.includes(o.key));
    const texts = selectedOptions.map((o) => String(o.text ?? ""));
    summaryLabel =
      texts.length === 0 ? placeholder || "Select options" : texts.join(", ");
  } else {
    const selectedOption = options.find((o) => o.key === sk);
    summaryLabel = selectedOption?.text || placeholder || "Select an option";
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const toggleKey = (opt: FormDropdownOption) => {
    const current = new Set<string>(selectedTexts);
    const text = String(opt.text ?? "");
    if (current.has(text)) {
      current.delete(text);
    } else {
      current.add(text);
    }
    onChange(Array.from(current));
  };

  const handleOptionClick = (opt: FormDropdownOption) => {
    if (isMulti) {
      toggleKey(opt);
    } else {
      onChange(opt.key);
      setOpen(false);
    }
  };

  // Shared custom dropdown UI for both single- and multi-select
  return (
    <div className="list-dropdown-wrapper" ref={containerRef}>
      <button
        type="button"
        id={id}
        className="form-select list-dropdown-multi-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="list-dropdown-multi-label">{summaryLabel}</span>
        <span className="list-dropdown-multi-caret"></span>
      </button>

      {open && (
        <div
          className="list-dropdown-multi-menu"
          role="listbox"
          aria-multiselectable={isMulti || undefined}
        >
          {options.map((o) => {
            const isSelected = isMulti
              ? selectedKeys.includes(o.key)
              : sk === o.key;
            return (
              <button
                key={o.key}
                type="button"
                className={
                  "list-dropdown-multi-option" +
                  (isSelected ? " list-dropdown-multi-option--selected" : "")
                }
                onClick={() => handleOptionClick(o)}
                role="option"
                aria-selected={isSelected}
              >
                <span className="list-dropdown-multi-option-check">
                  {isSelected ? "✓" : ""}
                </span>
                <span className="list-dropdown-multi-option-text">
                  {o.text}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ListDropdown;
